import { useCallback, useEffect, useState } from 'react';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { importsApi } from '../services/importsApi';
import { ImportCatalogItem, ImportResult, ImportSourceId } from '../types';

export const useImports = () => {
  const [sources, setSources] = useState<ImportCatalogItem[]>([]);
  const [selectedSourceId, setSelectedSourceId] = useState<ImportSourceId>('crs-data');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [absenteeFile, setAbsenteeFileState] = useState<File | null>(null);
  const [propertyFile, setPropertyFileState] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);

  const loadCatalog = useCallback(async () => {
    startLoading('imports-catalog', 'Loading import sources...');
    try {
      const response = await importsApi.getCatalog();
      setSources(response.sources);
      if (response.sources.length > 0) {
        setSelectedSourceId((current) =>
          response.sources.some((source) => source.id === current) ? current : response.sources[0].id
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load import catalog');
    } finally {
      stopLoading('imports-catalog');
    }
  }, [startLoading, stopLoading]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const selectSource = useCallback((sourceId: ImportSourceId) => {
    setSelectedSourceId(sourceId);
    setSelectedFiles([]);
    setAbsenteeFileState(null);
    setPropertyFileState(null);
    setImportResult(null);
    setError(null);
  }, []);

  const setFiles = useCallback((files: File[]) => {
    setSelectedFiles(files);
    setImportResult(null);
    setError(null);
  }, []);

  const setAbsenteeFile = useCallback((file: File | null) => {
    setAbsenteeFileState(file);
    setImportResult(null);
    setError(null);
  }, []);

  const setPropertyFile = useCallback((file: File | null) => {
    setPropertyFileState(file);
    setImportResult(null);
    setError(null);
  }, []);

  const runCrsImport = useCallback(async () => {
    if (selectedFiles.length === 0) {
      return;
    }
    startLoading('imports-crs', 'Importing CRS Data PDFs...');
    setError(null);
    try {
      const result = await importsApi.importCrsData(selectedFiles);
      setImportResult(result);
    } catch (err) {
      setImportResult(null);
      setError(err instanceof Error ? err.message : 'Failed to import CRS Data PDFs');
    } finally {
      stopLoading('imports-crs');
    }
  }, [selectedFiles, startLoading, stopLoading]);

  const runCrsAbsenteeMergeImport = useCallback(async () => {
    if (!absenteeFile || !propertyFile) {
      return;
    }
    startLoading('imports-crs-absentee-merge', 'Importing CRS absentee merge CSVs...');
    setError(null);
    try {
      const result = await importsApi.importCrsAbsenteeMerge(absenteeFile, propertyFile);
      setImportResult(result);
    } catch (err) {
      setImportResult(null);
      setError(err instanceof Error ? err.message : 'Failed to import CRS absentee merge CSVs');
    } finally {
      stopLoading('imports-crs-absentee-merge');
    }
  }, [absenteeFile, propertyFile, startLoading, stopLoading]);

  const clearImportResult = useCallback(() => {
    setImportResult(null);
  }, []);

  return {
    sources,
    selectedSourceId,
    selectSource,
    selectedFiles,
    setFiles,
    absenteeFile,
    propertyFile,
    setAbsenteeFile,
    setPropertyFile,
    importResult,
    error,
    globalLoading,
    loadingMessage,
    runCrsImport,
    runCrsAbsenteeMergeImport,
    clearImportResult
  };
};
